import { z } from 'zod';
import { useFormContext } from 'react-hook-form';
import { SchemaFormDefinition, SchemaFormFields, SelectField } from '@mews-commander/form';
import { BusinessError, FormInputField, lt } from '@mews/framework';
import { Stack, StackItem, Typography } from '@optimus-web/core';
import {
    ReservationContractExternalTemplateField,
    ReservationContractExternalTemplateFieldType,
    ReservationContractTemplateFieldIdentifier,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { addFieldPrefix } from '../utils';
import { TemplateFormValues } from '../types';
import { templateDataMappingOptions, TemplateFormStepFields } from './constants';

const TemplateMappingRow = ({ label, content }: { label: React.ReactNode; content: React.ReactNode }) => (
    <Stack distribution="space-between" spacing="200" itemAlignment="center">
        <StackItem grow maxWidth="295px">
            {label}
        </StackItem>
        <StackItem grow maxWidth="295px">
            {content}
        </StackItem>
    </Stack>
);

export const createDataMappingSchema = ({ fieldsData }: { fieldsData: ReservationContractExternalTemplateField[] }) =>
    z.object(
        Object.fromEntries(
            fieldsData.map(({ id }) => [
                addFieldPrefix(id),
                z.nativeEnum(ReservationContractTemplateFieldIdentifier).optional(),
            ])
        )
    );

export type DataMappingValues = z.infer<ReturnType<typeof createDataMappingSchema>>;

export const createDataMappingFormDefinition = ({
    fieldsData,
}: {
    fieldsData: ReservationContractExternalTemplateField[];
}): SchemaFormDefinition<ReturnType<typeof createDataMappingSchema>> => ({
    fields: Object.fromEntries(
        fieldsData.map((field, index) => {
            const showPageLabel = index === 0 || fieldsData[index - 1].pageNumber !== field.pageNumber;
            return [
                addFieldPrefix(field.id),
                {
                    render: (props) => (
                        <>
                            {showPageLabel && (
                                <Typography textStyle="bodyLargeStrong" as="h2">
                                    {lt('ContractTemplatePageLabel')} {field.pageNumber}
                                </Typography>
                            )}
                            <TemplateMappingRow
                                label={
                                    <Typography textStyle="bodyLarge" as="p">
                                        {field.label}
                                    </Typography>
                                }
                                content={
                                    <>
                                        {field.type !== ReservationContractExternalTemplateFieldType.Checkbox &&
                                            field.type !== ReservationContractExternalTemplateFieldType.Radio && (
                                                <SelectField
                                                    {...props}
                                                    options={templateDataMappingOptions}
                                                    showEmptyValue={false}
                                                />
                                            )}
                                        {field.type === ReservationContractExternalTemplateFieldType.Checkbox && (
                                            <FormInputField
                                                {...props}
                                                readOnly
                                                placeholder={lt('ContractTemplateTabIdentifierOptionLabelCheckbox')}
                                            />
                                        )}
                                        {field.type === ReservationContractExternalTemplateFieldType.Radio && (
                                            <FormInputField
                                                {...props}
                                                readOnly
                                                placeholder={lt('ContractTemplateTabIdentifierOptionLabelRadioGroup')}
                                            />
                                        )}
                                    </>
                                }
                            />
                        </>
                    ),
                },
            ];
        })
    ),
});

export const DataMappingForm = () => {
    const { watch } = useFormContext<TemplateFormValues>();
    const externalTemplateId = watch('templateOptionsFields.template');
    const { data: contractFieldsData } = useRepositorySuspenseQuery(
        'reservationContractExternalTemplates',
        'getAllFields',
        { externalTemplateId }
    );
    const fieldsData = contractFieldsData?.documentFields?.[0]?.fields.sort((a, b) =>
        a.pageNumber === b.pageNumber ? a.order - b.order : a.pageNumber - b.pageNumber
    );

    if (fieldsData == null) {
        throw new BusinessError('Fields data is not available');
    }

    return (
        <Stack vertical spacing="200">
            <Typography textStyle="titleLargeStrong" as="h2">
                {lt('ContractTemplateDataMappingTitle')}
            </Typography>
            <TemplateMappingRow
                label={
                    <Typography textStyle="bodyMedium" variant="tertiary" as="p">
                        {lt('ContractTemplateDataPlaceholderText')}
                    </Typography>
                }
                content={
                    <Typography textStyle="bodyMedium" variant="tertiary" as="p">
                        {lt('ContractTemplateDataFillWithInputText')}
                    </Typography>
                }
            />
            <SchemaFormFields
                schema={createDataMappingSchema({ fieldsData })}
                formDefinition={createDataMappingFormDefinition({ fieldsData })}
                namePrefix={TemplateFormStepFields.dataMappingFields}
            />
        </Stack>
    );
};
